import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { ChartTypes, FlamingoResponse, FlamingoTypes } from '@mode/shared/contract-common';
import {
  generateFormatFromFlatTableFormat,
  getDefaultFieldFormatMapFromColumns,
  getDefaultGeneralFormat,
  getDefaultTableColumnSettingsFromColumns,
  isPresent,
  makePagingResult,
  makeQueryPlanMetadataId,
} from '@mode/shared/util-js';
import { ObservabilityEvents, ObservabilityService } from '@mode/shared/util-observability';
import { FlamingoFacade, PlanMetadataFacade, PlanPaging } from '@mode/switch-views/contract';
import { PagingResult } from 'libs/shared/contract-common/src/lib/flamingo.types';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, skipUntil, switchMap, takeUntil, tap } from 'rxjs/operators';
/* eslint-disable @angular-eslint/component-selector */
@Component({
  selector: 'mode-flamingo-grid-ng',
  templateUrl: './flamingo-grid-ng.component.html',
  styleUrls: ['./flamingo-grid-ng.component.scss'],
})
export class FlamingoGridNgComponent implements OnInit, OnDestroy, OnChanges {
  // bindings
  @Input() csvLink!: string;
  @Input() executionTime = '';
  @Input() isFallbackData?: boolean;
  @Input() lastSuccessfulLabel = 'Run';
  @Input() lastSuccessfulRunAt = '';
  @Input() queryToken!: string;
  @Input() size = 0;
  @Input() sizeGridToFit = false;

  // outputs
  @Output() notebookSelected: EventEmitter<null> = new EventEmitter(); // Temporary until routing notebook transition can be called from child components

  // local state
  unsubscribe$: Subject<void>;
  queryToken$: BehaviorSubject<string>;
  loading$!: Observable<boolean>;
  failed$!: Observable<boolean>;
  success$!: Observable<boolean>;
  pageLoading$!: Observable<boolean>;
  flamingoError$!: Observable<FlamingoResponse.FlamingoErrorResponse>;
  records$!: Observable<FlamingoTypes.RecordsResult>;
  content$!: Observable<FlamingoTypes.RecordsResult['content']>;
  columns$!: Observable<FlamingoTypes.RecordsResult['columns']>;
  paging$!: Observable<FlamingoTypes.PagingResult>;
  format$!: Observable<ChartTypes.ChartFormat>;
  fieldFormats$!: Observable<ChartTypes.FieldFormatMap>;

  constructor(
    private flamingoFacade: FlamingoFacade,
    private planMetadataFacade: PlanMetadataFacade,
    private observabilityService: ObservabilityService
  ) {
    this.queryToken$ = new BehaviorSubject('');
    this.unsubscribe$ = new Subject();
  }

  ngOnInit(): void {
    const planMetadataId$ = this.queryToken$.pipe(
      map((queryToken) => {
        return makeQueryPlanMetadataId(queryToken);
      })
    );

    const planId$ = this.queryToken$.pipe(
      switchMap((queryToken) => this.flamingoFacade.createPlanIdFromRefId(queryToken)),
      distinctUntilChanged()
    );

    const state$ = planId$.pipe(switchMap((planId) => this.flamingoFacade.createStateFromPlanId(planId)));
    this.success$ = state$.pipe(
      map((state) => state === FlamingoTypes.PlanStatus.Succeeded),
      tap(() =>
        this.observabilityService.startChildSpan(ObservabilityEvents.TABLE_RENDER_TIME, ObservabilityEvents.QUERY_RUN)
      )
    );
    this.failed$ = state$.pipe(map((state) => state === FlamingoTypes.PlanStatus.Failed));
    this.loading$ = state$.pipe(
      map((state) => state === FlamingoTypes.PlanStatus.Loading),
      debounceTime(250)
    ); // Debouncing so short queries don't flash the loading message
    this.pageLoading$ = state$.pipe(map((state) => state === FlamingoTypes.PlanStatus.LoadingPage));

    const refPlanId$ = planMetadataId$.pipe(
      switchMap(
        () => planId$.pipe(filter((planId) => planId != null)),
        (planMetadataId, planId) => ({ planMetadataId, planId })
      )
    );

    this.flamingoError$ = planId$.pipe(
      filter((planId) => planId != null),
      switchMap((id) => this.flamingoFacade.createErrorFromPlanId(id)),
      takeUntil(this.unsubscribe$)
    );

    this.records$ = planId$.pipe(
      switchMap((planId) => this.flamingoFacade.createRecordsFromPlanId(planId)),
      filter((res) => res != null)
    ) as Observable<FlamingoTypes.RecordsResult>;

    this.paging$ = refPlanId$.pipe(
      switchMap(({ planId, planMetadataId }) =>
        combineLatest(
          this.planMetadataFacade.createPagingSelector(planMetadataId),
          this.flamingoFacade.createCountFromPlanId(planId).pipe(filter(isPresent)),
          (paging, count) => makePagingResult(paging as PlanPaging, count)
        )
      ),
      filter((p: any) => p != null),
      takeUntil(this.unsubscribe$)
    );

    this.columns$ = this.queryToken$.pipe(
      switchMap((queryToken) => this.flamingoFacade.createColumnsByRefId(queryToken)),
      skipUntil(this.success$),
      filter((columns) => columns != null),
      takeUntil(this.unsubscribe$)
    );

    this.format$ = this.columns$.pipe(
      map((columns) =>
        generateFormatFromFlatTableFormat({
          general: {
            ...getDefaultGeneralFormat(ChartTypes.FlatTableDefaultGeneralFormat.Results),
            sizingStyle: this.sizeGridToFit
              ? 'sizeToFit'
              : getDefaultGeneralFormat(ChartTypes.FlatTableDefaultGeneralFormat.Results).sizingStyle,
          },
          columns: getDefaultTableColumnSettingsFromColumns(columns),
        })
      )
    );

    this.fieldFormats$ = this.columns$.pipe(map((columns) => getDefaultFieldFormatMapFromColumns(columns)));

    this.content$ = this.records$.pipe(map((records) => records.content));

    // @TODO: (@anthonysimone) [Datasets] - Handle this elsewhere, validate where it's needed/if it's necessary
    // const reportCreatorEmail$ = this.rxStore.select(selectedReportCreatorEmailSelector);
    // const accountEmail$ = this.rxStore.select(accountEmailSelector);
    // this.reportEditLink$ = this.rxStore.select(selectedReportWebEditLinkSelector).pipe(map((link) => link));
    // this.userIsCreator$ = accountEmail$.pipe(
    //   withLatestFrom(reportCreatorEmail$),
    //   map(([accountEmail, creatorEmail]) => creatorEmail === accountEmail)
    // );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.queryToken) {
      this.queryToken$.next(this.queryToken);
    }
  }

  ngOnDestroy(): void {
    // Replace this with some general solution in the future.
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * Dispatch the paging action based on the results type context (with calcs or raw results).
   */
  setOffset(offset: number): void {
    this.planMetadataFacade.setOffsetRawResults(this.queryToken, offset);
  }

  /**
   * Dispatch the notebookSelected event
   *
   * Temporary until routing notebook transition can be called from child components.
   */
  goToNotebook() {
    this.notebookSelected.emit();
  }

  renderEnd() {
    this.observabilityService.endChildSpan(ObservabilityEvents.TABLE_RENDER_TIME);
    this.observabilityService.endTrace(ObservabilityEvents.QUERY_RUN);
  }
}
